import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, { useState } from 'react'
import { Group, iconSizes, Loader, Pagination, Select, Table } from '@common/ui'
import { IconArrowDown, IconArrowUp } from '@common/ui/icons'
import pageSizeOptions from '../../util/pagination/pageSizeOptions'
import PaginationModel from '../../util/pagination/PaginationModel'

const sortHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer',
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'inherit',
}

export type PagedTableProps<Row> = {
  columns: ColumnDef<Row, unknown>[]
  rows: readonly Row[]
  paginationModel: PaginationModel
  onPaginationModelChange: (paginationModel: PaginationModel) => void
  loading?: boolean
  getRowId?: (row: Row, index: number) => string
  'data-testid'?: string
}

const PagedTable = <Row,>({
  columns,
  rows,
  paginationModel,
  onPaginationModelChange,
  loading = false,
  getRowId,
  'data-testid': dataTestId,
}: PagedTableProps<Row>) => {
  const [sorting, setSorting] = useState<SortingState>([])

  const table = useReactTable({
    data: rows as Row[],
    columns,
    state: {
      sorting,
      pagination: { pageIndex: paginationModel.page, pageSize: paginationModel.pageSize },
    },
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const current = { pageIndex: paginationModel.page, pageSize: paginationModel.pageSize }
      const next = typeof updater === 'function' ? updater(current) : updater
      onPaginationModelChange({ page: next.pageIndex, pageSize: next.pageSize })
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId,
  })

  return (
    <div data-testid={dataTestId}>
      <Table.ScrollContainer minWidth={640}>
        <Table>
          <Table.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sorted = header.column.getIsSorted()
                  const canSort = header.column.getCanSort()
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())
                  return (
                    <Table.Th
                      key={header.id}
                      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                    >
                      {canSort ? (
                        <button type="button" style={sortHeaderStyle} onClick={header.column.getToggleSortingHandler()}>
                          {content}
                          {sorted === 'asc' && <IconArrowUp size={iconSizes.xs} stroke={1.5} />}
                          {sorted === 'desc' && <IconArrowDown size={iconSizes.xs} stroke={1.5} />}
                        </button>
                      ) : (
                        <Group gap="xxs" wrap="nowrap">
                          {content}
                        </Group>
                      )}
                    </Table.Th>
                  )
                })}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Group justify="center" p="md">
                    <Loader size="sm" />
                  </Group>
                </Table.Td>
              </Table.Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Table.Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Td>
                  ))}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Group justify="flex-end" gap="md" mt="sm">
        <Select
          w={90}
          size="xs"
          aria-label="Rows per page"
          data={Object.values(pageSizeOptions).map((size) => String(size))}
          value={String(paginationModel.pageSize)}
          onChange={(value) => {
            if (value) onPaginationModelChange({ page: 0, pageSize: Number(value) })
          }}
          allowDeselect={false}
        />
        <Pagination
          total={table.getPageCount()}
          value={paginationModel.page + 1}
          onChange={(page) => onPaginationModelChange({ ...paginationModel, page: page - 1 })}
        />
      </Group>
    </div>
  )
}

export default PagedTable
